import {
  doc, // Use doc() to get a DocumentReference
  onSnapshot,
} from "firebase/firestore";
import { firestore } from "../../../services/firebase"; // Ensure this path is correct

/**
 * Subscribes to real-time updates for a specific list of document IDs
 * within a given Firestore collection.
 *
 * @param {string} collectionName - The name of the Firestore collection (e.g., "branches").
 * @param {string[]} documentIds - An array of document IDs to listen to.
 * @param {function} setData - Callback function to set the fetched/updated data.
 *                             It will be called with an array of documents.
 * @returns {function} - An unsubscribe function to detach all listeners.
 */
const getSubDataForSpecificDocs = (
  collectionName,
  documentIds, // These are the IDs of the documents themselves
  setData
) => {
  // Filter out any invalid or empty document IDs and ensure uniqueness
  const validDocumentIds = Array.from(
    new Set(
      documentIds.filter(
        (id) => id && typeof id === "string" && id.trim() !== ""
      )
    )
  );

  if (validDocumentIds.length === 0) {
    setData([]); // No valid IDs to listen to
    console.warn(
      "getSubDataForSpecificDocs: No valid document IDs provided. No listeners attached."
    );
    return () => {}; // Return a no-op unsubscribe function
  }

  // This Map will store the current data for each document we're listening to.
  // Key: documentId, Value: document data object { id, ...data }
  const currentDocsData = new Map();

  // This array will store all the individual unsubscribe functions.
  const unsubscribers = [];

  // Helper function to update the parent component's state
  const updateParentState = () => {
    // Convert the Map values (our documents) into an array
    const newDataArray = Array.from(currentDocsData.values());
    setData(newDataArray);
  };

  validDocumentIds.forEach((docId) => {
    const documentRef = doc(firestore, collectionName, docId);

    const unsubscribe = onSnapshot(
      documentRef,
      (docSnap) => {
        if (docSnap.exists()) {
          // Document exists, add/update it in our map
          currentDocsData.set(docId, {
            id: docSnap.id,
            ...docSnap.data(),
          });
        } else {
          // Document was deleted or doesn't exist
          currentDocsData.delete(docId);
          console.log(
            `Document ${collectionName}/${docId} no longer exists or was deleted.`
          );
        }
        updateParentState(); // Update the UI with the latest aggregated data
      },
      (error) => {
        console.error(
          `Error listening to document ${collectionName}/${docId}: `,
          error
        );
        // Optionally, you might want to remove it from the list or set an error state
        currentDocsData.delete(docId);
        updateParentState();
      }
    );
    unsubscribers.push(unsubscribe);
  });

  // Return a master unsubscribe function that will call all individual unsubscribers
  return () => {
    console.log(
      `Unsubscribing from ${unsubscribers.length} document listeners in ${collectionName}.`
    );
    unsubscribers.forEach((unsub) => unsub());
    // Clear internal states (optional, but good practice if this function instance could be reused, though unlikely here)
    currentDocsData.clear();
    unsubscribers.length = 0;
  };
};

export default getSubDataForSpecificDocs;
